// src/components/store/sections/deal/Variant1Spotlight.tsx
"use client";

import Image from "next/image";
import Link from "next/link";
import {
  Star,
  ShoppingBag,
  Clock,
  Flame,
  ArrowRight,
  ShieldCheck,
  Truck,
  RotateCcw,
} from "lucide-react";
import { useProductTimer } from "./useProductTimer";

interface Props {
  products: any[];
  subdomain: string;
}

export default function Variant1Spotlight({ products, subdomain }: Props) {
  const resolveImageUrl = (raw?: string | null) => {
    if (!raw) return null;
    if (raw.startsWith("http://") || raw.startsWith("https://") || raw.startsWith("data:")) {
      if (raw.includes("127.0.0.1:8000")) {
        return raw.replace("127.0.0.1:8000", `${subdomain}.localhost:8000`);
      }
      return raw;
    }
    const clean = raw.startsWith("/") ? raw : `/${raw}`;
    return `http://${subdomain}.localhost:8000${clean}`;
  };

  const featuredDeal = products[0];
  const gridDeals = products.slice(1, 5);

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 py-6 font-sans select-none space-y-6">
      {/* Top Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="space-y-1">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-blue-50 text-blue-600 text-xs font-black">
            <Flame className="w-3.5 h-3.5 fill-blue-600" />
            <span>Today&apos;s Special</span>
          </div>
          <h2 className="text-2xl sm:text-3xl font-black text-slate-900 tracking-tight">
            Deals of the Day ({products.length})
          </h2>
          <p className="text-xs sm:text-sm text-slate-400 font-medium">
            Limited time offers • Grab your favorite products at unbeatable prices!
          </p>
        </div>

        <div className="flex items-center gap-3">
          <GlobalBadge targetProduct={featuredDeal} />
          <Link
            href="/products"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs shadow-md transition-all active:scale-95"
          >
            <span>View All Deals</span>
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>

      {/* Main Grid: Left Big Spotlight + Right 2x2 Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 items-stretch">
        {featuredDeal && (
          <div className="lg:col-span-5 flex flex-col">
            <SpotlightCard product={featuredDeal} resolveImageUrl={resolveImageUrl} />
          </div>
        )}

        <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
          {gridDeals.map((item: any) => (
            <GridItemCard key={item.id} product={item} resolveImageUrl={resolveImageUrl} />
          ))}
        </div>
      </div>
    </section>
  );
}

function SpotlightCard({ product, resolveImageUrl }: { product: any; resolveImageUrl: any }) {
  const timer = useProductTimer(product);
  const currentPrice = Number(product?.price || 0);
  const oldPrice = product?.compare_price ? Number(product.compare_price) : null;
  const discountPercent =
    oldPrice && oldPrice > currentPrice
      ? Math.round(((oldPrice - currentPrice) / oldPrice) * 100)
      : 33;
  const imgUrl = resolveImageUrl(product?.primary_image || product?.image);
  const slug = product?.slug || product?.id || "";

  return (
    <div className="relative w-full h-full rounded-[36px] p-6 sm:p-7 overflow-hidden text-white flex flex-col justify-between shadow-2xl bg-gradient-to-br from-indigo-950 via-purple-950 to-slate-950 border border-purple-800/40 group">
      <div className="relative z-10 flex items-start justify-between gap-3">
        <div className="space-y-2">
          <div className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-rose-500 text-white font-black text-xs shadow-md">
            <Flame className="w-3.5 h-3.5 fill-white" />
            <span>{discountPercent}% OFF</span>
          </div>
          <div>
            <span className="text-[11px] font-bold text-cyan-300 uppercase tracking-wider block">
              {product?.category_name || "Kids & Baby"}
            </span>
            <Link
              href={`/products/${slug}`}
              className="text-2xl sm:text-3xl font-black text-white hover:text-cyan-300 transition-colors line-clamp-1 block mt-0.5"
            >
              {product?.name || "GNG TOYS"}
            </Link>
          </div>
          <div className="flex items-center gap-1.5 text-amber-400 text-xs font-bold">
            <Star className="w-3.5 h-3.5 fill-amber-400" />
            <span>5.0 (124 reviews)</span>
          </div>
        </div>

        <div className="text-right">
          <span className="text-xs font-bold text-purple-300 block">Best Seller</span>
          <div className="text-2xl sm:text-3xl font-black text-white font-mono mt-0.5">
            ৳{currentPrice.toLocaleString()}
          </div>
          {oldPrice && (
            <span className="text-xs text-slate-400 line-through font-mono">
              ৳{oldPrice.toLocaleString()}
            </span>
          )}
        </div>
      </div>

      <div className="relative z-10 w-full h-56 sm:h-64 my-3 flex items-center justify-center">
        <div className="absolute bottom-2 w-52 sm:w-64 h-12 rounded-full bg-purple-600/30 blur-lg border border-purple-500/40" />
        <div className="relative w-48 sm:w-56 h-48 sm:h-56 group-hover:scale-105 transition-transform duration-500">
          {imgUrl ? (
            <Image
              src={imgUrl}
              alt={product?.name || "Spotlight"}
              fill
              unoptimized
              className="object-contain drop-shadow-[0_20px_25px_rgba(0,0,0,0.85)]"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-slate-500 font-bold">
              No Image
            </div>
          )}
        </div>
      </div>

      <div className="relative z-10 space-y-4 pt-2">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white/5 border border-white/10 rounded-2xl p-3 backdrop-blur-md">
          <div className="flex items-center gap-1.5 text-xs text-slate-300 font-bold">
            <Clock className="w-4 h-4 text-rose-400 animate-pulse" />
            <span>Hurry Up! Offer ends in:</span>
          </div>
          <div className="flex items-center gap-1.5 font-mono text-xs font-black">
            <span className="bg-slate-900/90 px-2.5 py-1 rounded-lg border border-white/10 text-white">
              {String(timer.hours).padStart(2, "0")}h
            </span>
            <span>:</span>
            <span className="bg-slate-900/90 px-2.5 py-1 rounded-lg border border-white/10 text-white">
              {String(timer.minutes).padStart(2, "0")}m
            </span>
            <span>:</span>
            <span className="bg-slate-900/90 px-2.5 py-1 rounded-lg border border-white/10 text-rose-400">
              {String(timer.seconds).padStart(2, "0")}s
            </span>
          </div>
        </div>

        <Link
          href={`/products/${slug}`}
          className="w-full py-3.5 rounded-2xl bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white font-black text-sm flex items-center justify-center gap-2 shadow-xl hover:shadow-indigo-500/30 transition-all active:scale-95"
        >
          <ShoppingBag className="w-4 h-4" />
          <span>Shop Now</span>
          <ArrowRight className="w-4 h-4" />
        </Link>

        <div className="flex items-center justify-between pt-2 text-[10px] text-slate-400 border-t border-white/10">
          <span className="flex items-center gap-1">
            <Truck className="w-3.5 h-3.5 text-cyan-400" /> Free Shipping
          </span>
          <span className="flex items-center gap-1">
            <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" /> Secure Payment
          </span>
          <span className="flex items-center gap-1">
            <RotateCcw className="w-3.5 h-3.5 text-amber-400" /> Easy Return
          </span>
        </div>
      </div>
    </div>
  );
}

function GridItemCard({ product, resolveImageUrl }: { product: any; resolveImageUrl: any }) {
  const timer = useProductTimer(product);
  const currentPrice = Number(product?.price || 0);
  const oldPrice = product?.compare_price ? Number(product.compare_price) : null;
  const discountPercent =
    oldPrice && oldPrice > currentPrice
      ? Math.round(((oldPrice - currentPrice) / oldPrice) * 100)
      : 25;
  const imgUrl = resolveImageUrl(product?.primary_image || product?.image);
  const slug = product?.slug || product?.id || "";

  return (
    <div className="bg-white rounded-3xl border border-slate-100 p-4 shadow-sm hover:shadow-lg transition-all duration-300 flex flex-col justify-between group">
      <div>
        <div className="flex items-center justify-between gap-2 mb-2">
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-rose-50 text-rose-600 font-black text-[10px]">
            <Flame className="w-3 h-3 fill-rose-600" />
            <span>{discountPercent}% OFF</span>
          </span>
          <span className="text-[10px] font-bold text-slate-400 truncate max-w-[110px]">
            {product?.category_name || "General"}
          </span>
        </div>

        <div className="relative w-full h-36 rounded-2xl bg-slate-50/70 overflow-hidden flex items-center justify-center p-2 mb-3">
          {imgUrl ? (
            <Image
              src={imgUrl}
              alt={product?.name || "Deal"}
              fill
              unoptimized
              className="object-contain group-hover:scale-105 transition-transform duration-300"
            />
          ) : (
            <div className="text-slate-300 text-xs font-bold">No Image</div>
          )}
        </div>

        <div className="space-y-1">
          <Link
            href={`/products/${slug}`}
            className="text-sm font-black text-slate-800 hover:text-blue-600 transition-colors line-clamp-1 block"
          >
            {product?.name || "Deal Item"}
          </Link>
          <div className="flex items-center gap-1 text-[11px] font-bold text-amber-500">
            <Star className="w-3 h-3 fill-amber-400" />
            <span>5.0 (89 reviews)</span>
          </div>
        </div>

        <div className="flex items-baseline gap-2 mt-2">
          <span className="text-lg font-black text-slate-900 font-mono">
            ৳{currentPrice.toLocaleString()}
          </span>
          {oldPrice && (
            <span className="text-xs text-slate-400 line-through font-mono">
              ৳{oldPrice.toLocaleString()}
            </span>
          )}
        </div>
      </div>

      <div className="space-y-2.5 mt-3 pt-2.5 border-t border-slate-100">
        <div className="flex items-center justify-between text-[10px] font-bold text-slate-500">
          <span className="text-rose-500 flex items-center gap-1">
            <Clock className="w-3 h-3" /> Ends in:
          </span>
          <span className="font-mono text-slate-800 font-black">
            {String(timer.hours).padStart(2, "0")}h : {String(timer.minutes).padStart(2, "0")}m :{" "}
            <span className="text-rose-600">{String(timer.seconds).padStart(2, "0")}s</span>
          </span>
        </div>

        <Link
          href={`/products/${slug}`}
          className="w-full py-2 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-black text-xs flex items-center justify-center gap-1.5 shadow-sm transition-all active:scale-95"
        >
          <ShoppingBag className="w-3.5 h-3.5" />
          <span>Shop Now</span>
        </Link>
      </div>
    </div>
  );
}

function GlobalBadge({ targetProduct }: { targetProduct: any }) {
  const timer = useProductTimer(targetProduct);
  return (
    <div className="hidden md:flex items-center gap-2 px-3.5 py-1.5 rounded-2xl bg-white border border-slate-200 shadow-sm text-xs font-bold">
      <Clock className="w-4 h-4 text-rose-500" />
      <span className="text-slate-500 font-semibold">Ends in:</span>
      <span className="font-mono font-black text-slate-800">
        {String(timer.hours).padStart(2, "0")}h {String(timer.minutes).padStart(2, "0")}m{" "}
        <span className="text-rose-600">{String(timer.seconds).padStart(2, "0")}s</span>
      </span>
    </div>
  );
}